import Uri from 'jsuri';
import qs from 'qs';

import { isAuthenticatedUser } from 'js/lib/user';

import { ON_SITE_SEARCH_PATH, SEO_ENTITY_SEARCH_PATH } from 'bundles/search-common/constants';
import { getShowRegistrationGate } from 'bundles/search-common/utils/experimentUtils';

export type Location = {
  pathname: string;
  hostname?: string;
};

const buildNewUri = ({
  pathname,
  hostname,
  searchQuery,
}: {
  searchQuery: string;
  pathname: string;
  hostname?: string;
}) => {
  let searchPath = ON_SITE_SEARCH_PATH;

  if (pathname && pathname !== ON_SITE_SEARCH_PATH) {
    searchPath = SEO_ENTITY_SEARCH_PATH;
  }

  const uri = new Uri()
    // NOTE: do not use `location.protocol` because our Node SSR rendering is proxied
    .setProtocol('https')
    .setHost(hostname || '')
    .setPath(searchPath);
  if (searchQuery) {
    uri.addQueryParam('query', searchQuery);
  }
  return uri;
};

// Export for testing
export const addOtherParamsToUri = ({ otherParams, uri }: { otherParams?: URLSearchParams; uri: Uri }) => {
  // Make sure we don't add duplicate params
  if (otherParams) {
    otherParams.delete('page');
    otherParams.delete('query');

    otherParams.forEach((value, key) => {
      uri.addQueryParam(key, value);
    });
  }
};

export const getCanonicalLinkHrefOverride = ({
  searchQuery,
  location,
  pageNumber = 1,
  otherParams,
}: {
  searchQuery: string;
  location: Location;
  pageNumber?: number;
  otherParams?: URLSearchParams;
}) => {
  const uri = buildNewUri({ pathname: location.pathname, hostname: location.hostname, searchQuery });
  if (pageNumber > 1) uri.addQueryParam('page', pageNumber);

  addOtherParamsToUri({ uri, otherParams });

  return uri.toString();
};

export const getAbsoluteNextPageHref = ({
  searchQuery,
  location,
  numTotalPages = 1,
  pageNumber = 1,
  otherParams,
}: {
  searchQuery: string;
  location: Location;
  numTotalPages?: number;
  pageNumber?: number;
  otherParams?: URLSearchParams;
}) => {
  // if on the last page, don't have a next page link
  if (pageNumber === numTotalPages) return undefined;

  const uri = buildNewUri({ pathname: location.pathname, hostname: location.hostname, searchQuery });
  uri.addQueryParam('page', pageNumber + 1);

  addOtherParamsToUri({ uri, otherParams });

  return uri.toString();
};

export const getAbsolutePrevPageHref = ({
  searchQuery,
  location,
  pageNumber = 1,
  otherParams,
}: {
  searchQuery: string;
  location: Location;
  pageNumber?: number;
  otherParams?: URLSearchParams;
}) => {
  // For the first page, there is no previous link
  if (pageNumber === 1) return undefined;

  const uri = buildNewUri({ pathname: location.pathname, hostname: location.hostname, searchQuery });

  // For the second page, the previous link doesn't need a page=1 query param
  if (pageNumber > 2) {
    uri.addQueryParam('page', pageNumber - 1);

    addOtherParamsToUri({ uri, otherParams });
  }

  return uri.toString();
};

export const createSeoURL = (query: string, page: number, activeFilters?: string[]) => {
  const queryParameters: Record<string, number | string | string[] | undefined> = {};
  queryParameters.query = query ?? undefined;
  const hasActiveFilters = (activeFilters?.length || 0) > 0;
  const isDefaultRoute = !query && page && page === 1 && hasActiveFilters;

  // we don't need filter SEARCH_FILTERS_FOR_URL_SYNC because activeFilters already filtered

  if (!isDefaultRoute) {
    if (page > 1) {
      queryParameters.page = page;
    }

    if (page >= 3 && !isAuthenticatedUser() && getShowRegistrationGate()) {
      queryParameters.authMode = 'signup';
    }

    if (hasActiveFilters) {
      // allows for multiple filters in same subcategory to be added to the pagnination url
      for (const filter of activeFilters ?? []) {
        const [category, label] = filter.split(':');

        if (typeof queryParameters === 'object') {
          // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          if (queryParameters[category]) {
            // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            if (Array.isArray(queryParameters[category])) {
              // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              (queryParameters[category] as string[]).push(label);
            } else {
              // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              queryParameters[category] = [queryParameters[category] as string, label];
            }
          } else {
            // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            queryParameters[category] = label;
          }
        }
      }
    }
  }

  // Replaces encoded spaces to the plus symbol to increase URL readability
  const queryString = qs.stringify(queryParameters, { arrayFormat: 'repeat' });

  return queryString;
};
